import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ModelTsContext } from "../context/modelTS.context";
import { AppContext } from "../context/app.context";

// Color palette for different locations
const LOCATION_COLORS = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff7300", // Orange
  "#00C49F", // Teal
  "#FFBB28", // Gold
  "#FF8042", // Coral
  "#0088FE", // Blue
  "#00C5CD", // Cyan
  "#FF6B6B", // Red
];

export const QueryPerformanceModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("line"); // 'line' or 'bar'
  const [isChartReady, setIsChartReady] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [daysFilter, setDaysFilter] = useState(30);
  const [metricType, setMetricType] = useState("avg"); // 'avg' or 'max'
  const [selectedLocations, setSelectedLocations] = useState([]); // Empty = all locations

  const context = useContext(ModelTsContext);
  const appContext = useContext(AppContext);

  const {
    attributesPerformanceResult,
    attributesPerformanceFetching,
    setShouldFetchAttributesPerformance,
    setAttributesPerformanceParams,
  } = context || {};

  const { darkMode } = appContext || {};
  const isDarkMode = darkMode || false;

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && !hasFetched) {
      setIsChartReady(false);
      setHasFetched(true);
      if (setShouldFetchAttributesPerformance && setAttributesPerformanceParams) {
        setAttributesPerformanceParams({
          daysFilter: daysFilter,
          locationFilter: selectedLocations.length > 0 ? selectedLocations : null,
        });
        setShouldFetchAttributesPerformance(true);
      }
    } else if (!isOpen) {
      setHasFetched(false);
      if (setShouldFetchAttributesPerformance) {
        setShouldFetchAttributesPerformance(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasFetched]);

  // Refetch when filters change
  useEffect(() => {
    if (isOpen && hasFetched && setAttributesPerformanceParams && setShouldFetchAttributesPerformance) {
      setIsChartReady(false);
      setAttributesPerformanceParams({
        daysFilter: daysFilter,
        locationFilter: selectedLocations.length > 0 ? selectedLocations : null,
      });
      setShouldFetchAttributesPerformance(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysFilter, selectedLocations]);

  // Set chart ready when data loads
  useEffect(() => {
    const hasData =
      attributesPerformanceResult?.data &&
      attributesPerformanceResult.data.length > 0 &&
      !attributesPerformanceFetching;

    if (hasData) {
      const timer = setTimeout(() => {
        setIsChartReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [attributesPerformanceResult, attributesPerformanceFetching]);

  // Wrapped in useMemo so the `|| []` fallback returns the SAME empty array
  // across renders — without it, the useMemos below that depend on rawData
  // would change deps every render (react-hooks/exhaustive-deps warning).
  const rawData = useMemo(
    () => attributesPerformanceResult?.data || [],
    [attributesPerformanceResult]
  );
  const availableLocations = attributesPerformanceResult?.locations || [];

  // Transform data for line chart (grouped by date, with location as series)
  const lineChartData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    // Group by date
    const byDate = {};
    rawData.forEach((row) => {
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date };
      }
      const key = metricType === "avg" ? "avgDurationMs" : "maxDurationMs";
      byDate[row.date][row.location] = row[key];
    });

    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [rawData, metricType]);

  // Transform data for bar chart (aggregate by location for selected date range)
  const barChartData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    // Aggregate by location
    const byLocation = {};
    rawData.forEach((row) => {
      if (!byLocation[row.location]) {
        byLocation[row.location] = {
          location: row.location,
          totalAvg: 0,
          totalMax: 0,
          count: 0,
          avgRecordCount: 0,
        };
      }
      byLocation[row.location].totalAvg += row.avgDurationMs * row.queryCount;
      byLocation[row.location].totalMax = Math.max(
        byLocation[row.location].totalMax,
        row.maxDurationMs
      );
      byLocation[row.location].count += row.queryCount;
      byLocation[row.location].avgRecordCount += row.avgRecordCount * row.queryCount;
    });

    return Object.values(byLocation)
      .map((loc) => ({
        location: loc.location,
        avgDurationMs: Math.round(loc.totalAvg / loc.count),
        maxDurationMs: loc.totalMax,
        queryCount: loc.count,
        avgRecordCount: Math.round(loc.avgRecordCount / loc.count),
      }))
      .sort((a, b) => b.avgDurationMs - a.avgDurationMs);
  }, [rawData]);

  // Get unique locations from line chart data for the legend
  const locationsInData = useMemo(() => {
    const locs = new Set();
    rawData.forEach((row) => locs.add(row.location));
    return Array.from(locs).sort();
  }, [rawData]);

  // Assign colors to locations
  const locationColorMap = useMemo(() => {
    const map = {};
    locationsInData.forEach((loc, idx) => {
      map[loc] = LOCATION_COLORS[idx % LOCATION_COLORS.length];
    });
    return map;
  }, [locationsInData]);

  // Theme colors
  const theme = {
    background: isDarkMode ? "#404040" : "#ffffff",
    text: isDarkMode ? "#ffffff" : "#000000",
    textSecondary: isDarkMode ? "#d1d5db" : "#666666",
    border: isDarkMode ? "#525252" : "#dddddd",
    tabActive: isDarkMode ? "#3b82f6" : "#2563eb",
    tabInactive: isDarkMode ? "#9ca3af" : "#666666",
    buttonBg: isDarkMode ? "#1f2937" : "#ffffff",
    buttonBorder: isDarkMode ? "#525252" : "#cccccc",
    inputBg: isDarkMode ? "#374151" : "#ffffff",
    overlayBg: isDarkMode ? "rgba(0, 0, 0, 0.75)" : "rgba(0, 0, 0, 0.5)",
    chartGrid: isDarkMode ? "#525252" : "#e5e7eb",
    tooltipBg: isDarkMode ? "#374151" : "#ffffff",
    tooltipBorder: isDarkMode ? "#525252" : "#cccccc",
    loadingSpinner: isDarkMode ? "#3b82f6" : "#2563eb",
  };

  // Custom tooltip for line chart
  const LineChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div
        style={{
          backgroundColor: theme.tooltipBg,
          border: `1px solid ${theme.tooltipBorder}`,
          padding: "12px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "bold", color: theme.text }}>
          {label}
        </p>
        {payload.map((entry, idx) => (
          <p
            key={idx}
            style={{ margin: "4px 0", color: entry.color, fontSize: "13px" }}
          >
            {entry.name}: {entry.value?.toLocaleString()}ms
          </p>
        ))}
      </div>
    );
  };

  // Custom tooltip for bar chart
  const BarChartTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: theme.tooltipBg,
          border: `1px solid ${theme.tooltipBorder}`,
          padding: "12px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "bold", color: theme.text }}>
          {data.location}
        </p>
        <p style={{ margin: "4px 0", color: theme.textSecondary, fontSize: "13px" }}>
          Avg Duration: {data.avgDurationMs?.toLocaleString()}ms
        </p>
        <p style={{ margin: "4px 0", color: theme.textSecondary, fontSize: "13px" }}>
          Max Duration: {data.maxDurationMs?.toLocaleString()}ms
        </p>
        <p style={{ margin: "4px 0", color: theme.textSecondary, fontSize: "13px" }}>
          Queries: {data.queryCount}
        </p>
        <p style={{ margin: "4px 0", color: theme.textSecondary, fontSize: "13px" }}>
          Avg Records: {data.avgRecordCount?.toLocaleString()}
        </p>
      </div>
    );
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations((prev) => {
      if (prev.includes(location)) {
        return prev.filter((l) => l !== location);
      } else {
        return [...prev, location];
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.overlayBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          backgroundColor: theme.background,
          borderRadius: "12px",
          padding: "24px",
          width: "95%",
          maxWidth: "1200px",
          maxHeight: "90vh",
          overflow: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0, color: theme.text }}>
            Attributes Query Performance by Location
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: theme.textSecondary,
              padding: "4px 8px",
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "20px",
            borderBottom: `1px solid ${theme.border}`,
            paddingBottom: "12px",
          }}
        >
          <button
            onClick={() => setActiveTab("line")}
            style={{
              background: "none",
              border: "none",
              fontSize: "15px",
              fontWeight: activeTab === "line" ? "600" : "400",
              color: activeTab === "line" ? theme.tabActive : theme.tabInactive,
              cursor: "pointer",
              padding: "8px 16px",
              borderBottom:
                activeTab === "line"
                  ? `2px solid ${theme.tabActive}`
                  : "2px solid transparent",
            }}
          >
            Trend Over Time
          </button>
          <button
            onClick={() => setActiveTab("bar")}
            style={{
              background: "none",
              border: "none",
              fontSize: "15px",
              fontWeight: activeTab === "bar" ? "600" : "400",
              color: activeTab === "bar" ? theme.tabActive : theme.tabInactive,
              cursor: "pointer",
              padding: "8px 16px",
              borderBottom:
                activeTab === "bar"
                  ? `2px solid ${theme.tabActive}`
                  : "2px solid transparent",
            }}
          >
            Location Comparison
          </button>
        </div>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "20px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Days Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ color: theme.textSecondary, fontSize: "14px" }}>
              Time Range:
            </label>
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(parseInt(e.target.value))}
              style={{
                padding: "6px 32px 6px 8px",
                borderRadius: "4px",
                border: `1px solid ${theme.buttonBorder}`,
                backgroundColor: theme.inputBg,
                color: theme.text,
                fontSize: "13px",
                minWidth: "130px",
                height: "32px",
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {/* Metric Type (for line chart) */}
          {activeTab === "line" && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ color: theme.textSecondary, fontSize: "14px" }}>
                Metric:
              </label>
              <select
                value={metricType}
                onChange={(e) => setMetricType(e.target.value)}
                style={{
                  padding: "6px 32px 6px 8px",
                  borderRadius: "4px",
                  border: `1px solid ${theme.buttonBorder}`,
                  backgroundColor: theme.inputBg,
                  color: theme.text,
                  fontSize: "13px",
                  minWidth: "150px",
                  height: "32px",
                }}
              >
                <option value="avg">Average Duration</option>
                <option value="max">Max Duration</option>
              </select>
            </div>
          )}

          {/* Location Filter */}
          {availableLocations.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <label style={{ color: theme.textSecondary, fontSize: "14px" }}>
                Locations:
              </label>
              <button
                onClick={() => setSelectedLocations([])}
                style={{
                  padding: "4px 10px",
                  borderRadius: "4px",
                  border: `1px solid ${theme.buttonBorder}`,
                  backgroundColor:
                    selectedLocations.length === 0 ? theme.tabActive : theme.buttonBg,
                  color: selectedLocations.length === 0 ? "white" : theme.text,
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                All
              </button>
              {availableLocations.map((loc) => (
                <button
                  key={loc}
                  onClick={() => handleLocationToggle(loc)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "4px",
                    border: `1px solid ${locationColorMap[loc] || theme.buttonBorder}`,
                    backgroundColor: selectedLocations.includes(loc)
                      ? locationColorMap[loc] || theme.tabActive
                      : theme.buttonBg,
                    color: selectedLocations.includes(loc) ? "white" : theme.text,
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  {loc}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Chart */}
        <div style={{ width: "100%", height: "500px", minHeight: "500px" }}>
          {attributesPerformanceFetching || !isChartReady ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    display: "inline-block",
                    width: "48px",
                    height: "48px",
                    border: `4px solid ${theme.loadingSpinner}`,
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "16px",
                  }}
                ></div>
                <p style={{ color: theme.textSecondary }}>Loading performance data...</p>
              </div>
            </div>
          ) : rawData.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div style={{ textAlign: "center", color: theme.textSecondary }}>
                <p style={{ fontSize: "18px", marginBottom: "8px" }}>No Data Available</p>
                <p style={{ fontSize: "14px" }}>
                  No attributes query performance data found for the selected time range.
                </p>
                <p style={{ fontSize: "13px", marginTop: "12px", color: theme.tabInactive }}>
                  Note: Location data is only available for logs created after v01-05-2026-03.
                </p>
              </div>
            </div>
          ) : activeTab === "line" ? (
            <ResponsiveContainer width="100%" height={500} minWidth={300}>
              <LineChart
                data={lineChartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 11, fill: theme.text }}
                  stroke={theme.text}
                />
                <YAxis
                  label={{
                    value: metricType === "avg" ? "Avg Duration (ms)" : "Max Duration (ms)",
                    angle: -90,
                    position: "insideLeft",
                    fill: theme.text,
                  }}
                  tick={{ fill: theme.text }}
                  stroke={theme.text}
                />
                <Tooltip content={<LineChartTooltip />} />
                <Legend wrapperStyle={{ color: theme.text }} />
                {locationsInData.map((location) => (
                  <Line
                    key={location}
                    type="monotone"
                    dataKey={location}
                    name={location}
                    stroke={locationColorMap[location]}
                    strokeWidth={2}
                    dot={{ r: 4, fill: locationColorMap[location] }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={500} minWidth={300}>
              <BarChart
                data={barChartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} />
                <XAxis
                  type="number"
                  tick={{ fill: theme.text }}
                  stroke={theme.text}
                  label={{
                    value: "Duration (ms)",
                    position: "insideBottom",
                    offset: -5,
                    fill: theme.text,
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="location"
                  tick={{ fill: theme.text, fontSize: 12 }}
                  stroke={theme.text}
                  width={60}
                />
                <Tooltip content={<BarChartTooltip />} />
                <Legend wrapperStyle={{ color: theme.text }} />
                <Bar
                  dataKey="avgDurationMs"
                  name="Avg Duration"
                  fill="#8884d8"
                  radius={[0, 4, 4, 0]}
                />
                <Bar
                  dataKey="maxDurationMs"
                  name="Max Duration"
                  fill="#ff7300"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Summary Stats */}
        {rawData.length > 0 && (
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
              borderRadius: "8px",
              display: "flex",
              gap: "32px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <span style={{ color: theme.textSecondary, fontSize: "13px" }}>
                Total Data Points:
              </span>
              <span
                style={{
                  color: theme.text,
                  fontWeight: "600",
                  marginLeft: "8px",
                  fontSize: "14px",
                }}
              >
                {rawData.reduce((sum, r) => sum + r.queryCount, 0).toLocaleString()}
              </span>
            </div>
            <div>
              <span style={{ color: theme.textSecondary, fontSize: "13px" }}>
                Locations:
              </span>
              <span
                style={{
                  color: theme.text,
                  fontWeight: "600",
                  marginLeft: "8px",
                  fontSize: "14px",
                }}
              >
                {locationsInData.length}
              </span>
            </div>
            <div>
              <span style={{ color: theme.textSecondary, fontSize: "13px" }}>
                Date Range:
              </span>
              <span
                style={{
                  color: theme.text,
                  fontWeight: "600",
                  marginLeft: "8px",
                  fontSize: "14px",
                }}
              >
                {lineChartData.length > 0
                  ? `${lineChartData[0].date} to ${lineChartData[lineChartData.length - 1].date}`
                  : "N/A"}
              </span>
            </div>
            <div>
              <span style={{ color: theme.textSecondary, fontSize: "13px" }}>
                Overall Avg:
              </span>
              <span
                style={{
                  color: theme.text,
                  fontWeight: "600",
                  marginLeft: "8px",
                  fontSize: "14px",
                }}
              >
                {Math.round(
                  rawData.reduce((sum, r) => sum + r.avgDurationMs * r.queryCount, 0) /
                    rawData.reduce((sum, r) => sum + r.queryCount, 0)
                ).toLocaleString()}
                ms
              </span>
            </div>
            <div>
              <span style={{ color: theme.textSecondary, fontSize: "13px" }}>
                Overall Max:
              </span>
              <span
                style={{
                  color: "#ff7300",
                  fontWeight: "600",
                  marginLeft: "8px",
                  fontSize: "14px",
                }}
              >
                {Math.max(...rawData.map((r) => r.maxDurationMs)).toLocaleString()}ms
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryPerformanceModal;
